import Popover from "@components/ui/popover";
import { ScrollArea } from "@components/ui/scroll-area";
import { cn } from "@lib/cn";
import { useAnalyticsQuery } from "@pages/analytics/hooks";
import { useAnalyticsStoreShallow } from "@pages/analytics/store/analytics.store";
import {
  ANALYTICS_QUERY,
  type TReadersByLocation,
} from "@pages/analytics/types";
import {
  IconCheck,
  IconSearch,
  IconSelector,
  IconX,
} from "@tabler/icons-react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

export const LocationFilter = memo(() => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const { location, setLocation } = useAnalyticsStoreShallow((s) => ({
    location: s.location,
    setLocation: s.setLocation,
  }));

  const { data: response, isLoading } = useAnalyticsQuery({
    query: [
      ANALYTICS_QUERY.READERS_BY_DATE,
      ANALYTICS_QUERY.READERS_BY_LOCATION,
    ],
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 250);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const handleLocationChange = useCallback(
    (s: string) => {
      setLocation(s);
    },
    [setLocation],
  );

  const locations = useMemo(() => {
    const data: TReadersByLocation[] =
      (response?.[
        ANALYTICS_QUERY.READERS_BY_LOCATION
      ] as TReadersByLocation[]) || [];

    const seen = new Set<string>();
    return data
      .filter((item) => Boolean(item.city))
      .filter((item) => {
        // Unique by city only
        const key = item.city ?? "";
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [response]);

  // Filter cached locations by search term
  const filteredLocations = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return locations;
    const search = debouncedSearchTerm.toLowerCase();
    return locations.filter((item) =>
      item.city?.toLowerCase().includes(search),
    );
  }, [locations, debouncedSearchTerm]);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setTimeout(() => {
        setSearchTerm("");
        setDebouncedSearchTerm("");
      }, 250);
    }
  }, []);

  return (
    <Popover
      open={isOpen}
      onOpenChange={handleOpenChange}
      placement="bottom-start"
      triggerWidth
    >
      <Popover.Trigger
        className={cn(
          "tw-flex tw-h-9 tw-cursor-pointer tw-max-w-40 active:!tw-outline-offset-0 active:!tw-outline-1 active:!tw-outline-primary tw-items-center tw-shadow-2xs tw-gap-1 tw-rounded-lg tw-outline !tw-outline-1 !tw-outline-blue/5 tw-bg-white tw-px-2 tw-transition-colors !tw-outline-offset-0 tw-duration-100 hover:!tw-outline-primary -xl:tw-w-full -xl:tw-max-w-[700px]",
          {
            "!tw-outline-primary": isOpen || !!location,
          },
        )}
      >
        <span className="tw-block tw-px-1 tw-font-sans tw-truncate tw-text-sm tw-font-medium tw-text-blue">
          {location || "Location"}
        </span>
        <IconSelector className="tw-size-4 tw-text-blue-500 tw-ml-auto" />
        {location && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setLocation("");
              setIsOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                setLocation("");
                setIsOpen(false);
              }
            }}
            role="button"
            tabIndex={0}
            className="tw-ml-1 tw-flex tw-h-full tw-items-center tw-justify-center tw-border-l tw-border-solid tw-border-primary tw-pl-2"
          >
            <IconX className="tw-size-4 tw-text-primary" />
          </div>
        )}
      </Popover.Trigger>
      <Popover.Content className="tw-rounded-lg tw-outline !tw-w-auto !tw-outline-1 !tw-outline-blue/5 tw-bg-white tw-shadow-md">
        <div className="tw-relative tw-h-9 tw-px-3 tw-border-b tw-border-b-solid tw-border-b-blue-100">
          <IconSearch className="tw-size-4 tw-text-blue-500 tw-absolute tw-top-1/2 -tw-translate-y-1/2 tw-left-3" />
          <input
            type="text"
            placeholder="Search location"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="tw-pl-6 tw-outline-none tw-w-full tw-h-full tw-rounded-t-lg tw-text-sm tw-font-normal tw-text-blue placeholder:tw-text-blue-500"
          />
        </div>
        <ScrollArea maxHeight={250}>
          <div className="tw-p-1 tw-max-w-full tw-min-w-0 tw-space-y-0.5">
            {isLoading ? (
              <LocationSkeleton />
            ) : filteredLocations.length === 0 ? (
              <div className="tw-py-3 tw-text-center tw-text-sm tw-text-blue-500">
                {debouncedSearchTerm
                  ? "No locations found"
                  : "No locations available"}
              </div>
            ) : (
              filteredLocations.map((s) => (
                <button
                  type="button"
                  key={s.name}
                  onClick={() => handleLocationChange(s.name)}
                  className={cn(
                    "tw-flex tw-h-9 tw-w-52 tw-cursor-pointer tw-items-center tw-justify-between tw-rounded tw-px-2 tw-font-sans tw-text-sm tw-text-blue tw-outline-none tw-transition-colors hover:tw-bg-blue-50",
                    {
                      "tw-bg-blue-100 hover:tw-bg-blue-100":
                        location === s.name,
                    },
                  )}
                >
                  <span className="tw-flex-1 tw-min-w-0 tw-text-left tw-truncate tw-whitespace-nowrap">
                    {s.name}
                  </span>
                  {location === s.name && (
                    <IconCheck className="tw-size-4 tw-text-blue tw-ml-3 tw-flex-shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </ScrollArea>
      </Popover.Content>
    </Popover>
  );
});

const LOCATION_SKELETON_WIDTHS = [58, 84, 112, 40, 69];

const LocationSkeleton = () => {
  return (
    <>
      {LOCATION_SKELETON_WIDTHS.map((width) => (
        <div
          key={width}
          className="tw-flex tw-h-9 tw-w-full tw-items-center tw-justify-between tw-rounded tw-px-2 tw-animate-skeleton-loading"
        >
          <span
            className="tw-block tw-h-2 tw-w-24 tw-rounded-lg tw-bg-blue-100"
            style={{ width: `${width}%` }}
          />
        </div>
      ))}
    </>
  );
};
